import { S3Client, GetObjectCommand } from '@aws-sdk/client-s3';
import { CognitoIdentityClient } from '@aws-sdk/client-cognito-identity';
import { fromCognitoIdentityPool } from '@aws-sdk/credential-provider-cognito-identity';

import { getAppConfig } from '../../context/AppConfigContext';
import logger from '../../utils/logger';
import logError from '../../utils/logError';

// Retrieve the current Cognito user session
const getCurrentUserSession = (userPool) => {
  const cognitoUser = userPool.getCurrentUser();
  if (!cognitoUser) {
    const noUserError = new Error('No current user found in User Pool.');
    logger.error(logError(noUserError));
    return Promise.reject(noUserError);
  }
  return new Promise((resolve, reject) => {
    cognitoUser.getSession((err, session) => {
      if (err) {
        logger.error(logError(err, { action: 'getSession' }));
        reject(err);
      } else if (session.isValid()) {
        resolve(session);
      } else {
        const sessionError = new Error('Session is invalid');
        logger.error(logError(sessionError));
        reject(sessionError);
      }
    });
  });
};

// Initialize the S3 client with credentials from Cognito Identity Pool
const initializeS3Client = ({ region, userPoolId, identityPoolId, token }) => {
  const s3 = new S3Client({
    region,
    credentials: fromCognitoIdentityPool({
      client: new CognitoIdentityClient({ region }),
      identityPoolId,
      logins: {
        [`cognito-idp.${region}.amazonaws.com/${userPoolId}`]: token,
      },
    }),
  });
  return s3;
};

// Safely parse JSON content
const parseJSON = (content) => {
  try {
    const parsedContent = JSON.parse(content);
    return parsedContent;
  } catch (error) {
    logger.error(logError(error, { action: 'parseCompatibilityJson' }));
    throw new Error('Failed to parse JSON content');
  }
};

// Function to fetch compatible versions from S3
export const compatibleVersions = async (userPool) => {
  try {
    const { cognito, compatibilityStorage } = getAppConfig();
    const { userPoolId, region, identityPoolId } = cognito;
    const { bucketName, fileKey } = compatibilityStorage;

    if (!userPool) {
      throw new Error('Missing Cognito user pool instance');
    }
    if (!userPoolId || !region || !identityPoolId) {
      throw new Error('Incomplete Cognito configuration');
    }
    if (!bucketName || !fileKey) {
      throw new Error('Missing compatibility storage configuration');
    }

    const session = await getCurrentUserSession(userPool);
    const token = session.getIdToken().getJwtToken();

    // Initialize S3 client
    const s3 = initializeS3Client({ region, userPoolId, identityPoolId, token });
    const params = { Bucket: bucketName, Key: fileKey };

    // Fetch the object from S3
    const data = await s3.send(new GetObjectCommand(params));

    if (!data.Body?.transformToString) {
      throw new Error('Unsupported data.Body type');
    }
    const text = await data.Body.transformToString();

    // Parse and return JSON
    return parseJSON(text);
  } catch (error) {
    logger.error(logError(error, { action: 'fetchCompatibleVersions' }));
    throw error;
  }
};
